/**
 * Oath Power Comment Service
 * API calls for power comment operations
 * Routes to /oath/powers/* and /oath/comments/* endpoints
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// ============================================
// CONSTANTS
// ============================================

export const COMMENT_MAX_LENGTH = 4000;
export const COMMENT_WARNING_THRESHOLD = 3500;
export const DEFAULT_COMMENTS_PAGE_SIZE = 20;

// ============================================
// AUTH HELPERS
// ============================================

/**
 * Get auth headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Authenticated fetch wrapper with auto-refresh on 401
 */
const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  // If unauthorized, try to refresh token and retry
  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      // Retry with new token
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    }
  }

  return response;
};

// ============================================
// RETRIEVE COMMENTS
// ============================================

/**
 * Get paginated comments for a power
 * @param {string} powerNo - The power number
 * @param {number} [pageNumber=1] - Page number (min 1)
 * @param {number} [pageSize=20] - Page size (min 1, max 100)
 * @returns {Promise<{success: boolean, data: {items: PowerComment[], totalCount: number, pageNumber: number, pageSize: number, totalPages: number}, error?: string}>}
 */
export const getPowerComments = async (powerNo, pageNumber = 1, pageSize = DEFAULT_COMMENTS_PAGE_SIZE) => {
  try {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });
    const response = await authFetch(
      `${BASE_URL}oath/powers/${encodeURIComponent(powerNo)}/comments?${params}`
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.message || 'Failed to get comments',
        data: { items: [], totalCount: 0, pageNumber: 1, pageSize, totalPages: 0 },
      };
    }

    const json = await response.json();
    const data = json.data || json.result || json;
    return {
      success: true,
      data: {
        items: data.items || [],
        totalCount: data.totalCount || 0,
        pageNumber: data.pageNumber || pageNumber,
        pageSize: data.pageSize || pageSize,
        totalPages: data.totalPages || 0,
      },
    };
  } catch (error) {
    console.error('Get power comments error:', error);
    return {
      success: false,
      error: 'Network error',
      data: { items: [], totalCount: 0, pageNumber: 1, pageSize, totalPages: 0 },
    };
  }
};

// ============================================
// CREATE COMMENTS
// ============================================

/**
 * Create a comment on a single power
 * @param {Object} params
 * @param {string} params.powerNumber - The power number
 * @param {string} params.comment - Comment text (max 4000 chars)
 * @param {boolean} [params.contactAgent=false] - If true, emails the regional Pod team
 * @returns {Promise<{success: boolean, data?: PowerComment, error?: string}>}
 */
export const createComment = async ({ powerNumber, comment, contactAgent = false }) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/comments`, {
      method: 'POST',
      body: JSON.stringify({ powerNumber, comment, contactAgent }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.message || 'Failed to add comment' };
    }

    const json = await response.json();
    const data = json.data || json.result || json;
    return { success: true, data };
  } catch (error) {
    console.error('Create comment error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Create a comment on multiple powers (same comment text)
 * @param {Object} params
 * @param {string[]} params.powerNumbers - Array of power numbers
 * @param {string} params.comment - Comment text (max 4000 chars)
 * @param {boolean} [params.contactAgent=false] - If true, emails Pod team(s) grouped by region
 * @returns {Promise<{success: boolean, data?: PowerComment[], count?: number, requestedCount?: number, error?: string}>}
 */
export const createBulkComment = async ({ powerNumbers, comment, contactAgent = false }) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/comments/bulk`, {
      method: 'POST',
      body: JSON.stringify({ powerNumbers, comment, contactAgent }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.message || 'Failed to add comments' };
    }

    const json = await response.json();
    return {
      success: true,
      data: json.data || [],
      count: json.count,
      requestedCount: json.requestedCount,
    };
  } catch (error) {
    console.error('Create bulk comment error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * @typedef {Object} PowerComment
 * @property {number} commentId - Unique comment ID
 * @property {string} comment - Comment text
 * @property {string|null} subject - Optional subject line
 * @property {'AGT'|'SYS'|'USR'} entryType - Entry type code
 * @property {string} entryTypeDescription - Human-readable entry type
 * @property {string} severity - Severity code (e.g., 'L')
 * @property {string} recordedBy - Username who recorded the comment
 * @property {string} dateRecorded - ISO 8601 date string
 */
